import { Box, Breadcrumbs, Divider, IconButton, Link as MuiLink, Snackbar, SnackbarCloseReason, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { FormElement, FieldWrapper } from '@progress/kendo-react-form';
import { Checkbox, TextBox } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Hint, Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { useState } from 'react';
import { Link as RouterLink, useLoaderData, useNavigate, useParams } from 'react-router-dom';
import { fetchApi } from 'core/fetchApi';
import { UsersResponse } from './index';

const UserProfile = () => {
  const data = useLoaderData() as UsersResponse;
  const { carrierContactID } = useParams();
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState('');

  const user = data.Users.find(x => x.CarrierContactID === Number(carrierContactID));

  const [firstName, setFirstName] = useState(user?.FirstName || '');
  const [lastName, setLastName] = useState(user?.LastName || '');
  const [title, setTitle] = useState(user?.Title || '');
  const [contactType, setContactType] = useState(user?.ContactType || 0);
  const [phoneNumber, setPhoneNumber] = useState(user?.PhoneNumber || '');
  const [phoneNumberExtension, setPhoneNumberExtension] = useState(user?.PhoneNumberExtension || '');
  const [sendTruckAvailability, setSendTruckAvailability] = useState(user?.SendTruckAvailability || false);
  const [sendOfferCancellations, setSendOfferCancellations] = useState(user?.SendOfferCancellations || false);

  const handleCloseSnackbar = (_: Event | React.SyntheticEvent, reason: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbar('');
  };

  const _save = () => {
    fetchApi<{ Success: boolean, ErrorMessage: string }>({
      url: 'UpdateUser',
      method: 'POST',
      payload: {
        CarrierContactID: user?.CarrierContactID,
        FirstName: firstName,
        LastName: lastName,
        Title: title,
        ContactType: contactType,
        PhoneNumber: phoneNumber,
        PhoneNumberExtension: phoneNumberExtension,
        SendTruckAvailability: sendTruckAvailability,
        SendOfferCancellations: sendOfferCancellations,
      }
    })
      .then((response) => {
        if (!response.Success) {
          setSnackbar(response.ErrorMessage);
          return;
        }
        navigate('/users');
      })
      .catch(() => {
        setSnackbar('Error saving user');
      });
  };

  const _retire = () => {
    if (!confirm(`Retire ${user?.FullName || user?.EMailAddress}? They will no longer be able to sign in or receive load emails.`)) return;

    fetchApi<{ Success: boolean, ErrorMessage: string }>({ url: `RetireUser/${user?.CarrierContactID}`, method: 'POST' })
      .then((response) => {
        if (!response.Success) {
          setSnackbar(response.ErrorMessage);
          return;
        }
        navigate('/users');
      })
      .catch(() => {
        setSnackbar('Error retiring user');
      });
  };

  if (!user) {
    return <Box>
      <Typography variant="body1">This user was not found. They may have been retired.</Typography>
      <br />
      <MuiLink component={RouterLink} to="/users" underline="hover">Back to Users</MuiLink>
    </Box>;
  }

  return <>
    <Box sx={{ maxWidth: 600, pb: 4 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1 }}>
        <MuiLink component={RouterLink} to="/users" underline="hover" color="inherit">Users</MuiLink>
        <Typography color="text.primary">
          {user.FullName || user.EMailAddress}{user.IsSelf && ' (you)'}
        </Typography>
      </Breadcrumbs>
      <Divider sx={{ my: 2 }} />
      <FormElement>
        <FieldWrapper>
          <Label editorId="EMailAddress" className="k-form-label">
            Email Address
          </Label>
          <div className="k-form-field-wrap">
            <TextBox id="EMailAddress" value={user.EMailAddress} disabled />
            <Hint>Email addresses are used to sign in and cannot be changed here.</Hint>
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="FirstName" className="k-form-label">
            First Name
          </Label>
          <div className="k-form-field-wrap">
            <TextBox
              id="FirstName"
              value={firstName}
              onChange={(e) => setFirstName(e.value as string)}
              maxLength={20}
            />
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="LastName" className="k-form-label">
            Last Name
          </Label>
          <div className="k-form-field-wrap">
            <TextBox
              id="LastName"
              value={lastName}
              onChange={(e) => setLastName(e.value as string)}
              maxLength={20}
            />
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="Title" className="k-form-label">
            Title
          </Label>
          <div className="k-form-field-wrap">
            <TextBox
              id="Title"
              value={title}
              onChange={(e) => setTitle(e.value as string)}
              maxLength={20}
            />
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="ContactType" className="k-form-label">
            Contact Type
          </Label>
          <div className="k-form-field-wrap">
            <DropDownList
              id="ContactType"
              data={data.ContactTypes}
              textField="Name"
              dataItemKey="ID"
              value={data.ContactTypes.find(x => x.ID === contactType) || null}
              onChange={(e) => setContactType(e.target.value.ID)}
            />
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="PhoneNumber" className="k-form-label">
            Phone Number
          </Label>
          <div className="k-form-field-wrap">
            <TextBox
              id="PhoneNumber"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.value as string)}
              placeholder="+13135551212"
              maxLength={51}
            />
            <Hint>Include the country code, for example +13135551212.</Hint>
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="PhoneNumberExtension" className="k-form-label">
            Phone Extension
          </Label>
          <div className="k-form-field-wrap">
            <TextBox
              id="PhoneNumberExtension"
              value={phoneNumberExtension}
              onChange={(e) => setPhoneNumberExtension(e.value as string)}
              maxLength={4}
            />
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="SendTruckAvailability" className="k-form-label">
            Email Monthly Performance Report
          </Label>
          <Checkbox
            id="SendTruckAvailability"
            checked={sendTruckAvailability}
            onChange={(e) => setSendTruckAvailability(e.value)}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label editorId="SendOfferCancellations" className="k-form-label">
            Email if Awarding Another Carrier
          </Label>
          <Checkbox
            id="SendOfferCancellations"
            checked={sendOfferCancellations}
            onChange={(e) => setSendOfferCancellations(e.value)}
          />
        </FieldWrapper>
      </FormElement>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          themeColor="warning"
          disabled={user.IsSelf}
          title={user.IsSelf ? 'You cannot retire your own user' : undefined}
          onClick={_retire}
        >
          Retire
        </Button>
        <Button themeColor="primary" onClick={_save}>Save</Button>
      </Box>
    </Box>
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      open={!!snackbar}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      message={snackbar}
      action={
        <IconButton size="small" aria-label="close" color="inherit" onClick={(e) => handleCloseSnackbar(e, 'timeout')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    />
  </>;
}

export default UserProfile;
